import { ArrowLeft, Bell, Check, ChevronDown, ChevronRight, KeyRound, Languages, LogOut, Menu as MenuIcon, Monitor, Moon, Rows3, Search, ShieldCheck, SpeakerSimpleHigh, SpeakerSimpleX, Sun, User as UserIcon } from '@/design-system/icons'
import { useState, useSyncExternalStore } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, IconButton, Input, Kbd, Menu, Modal, cn, notify, playAlertSound } from '@/design-system'
import { useT } from '@/i18n'
import { useNavBadges } from '@/navigation/badges'
import { useActiveNav } from '@/navigation/useNavigation'
import { authService } from '@/services/authService'
import { useAuth, useCan } from '@/stores/auth'
import { useUi } from '@/stores/ui'
import { pick } from '@/domain/extract'
import { InstallButton } from './AppInstall'
import { useTabs } from '@/stores/tabs'
import { MobileTabsSheet } from './workspace/MobileTabs'
import { useTabLink } from './workspace/tabMeta'

/** True when the app runs installed (its own window, no browser bar) — no browser back button there. */
export const isInstalledApp = () =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.matchMedia?.('(display-mode: window-controls-overlay)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true)

/**
 * The browser's back, for the installed app (owner, 2026-10-01: "لما يعملو تسطيب للسيستم علي لاب توب ... مفيش زر
 * للعوده"): installed, the app has its own window and no browser bar, so the way back has to be ours.
 */
export function BackButton() {
  const { locale } = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menuOrigin = useUi((s) => s.menuOrigin)
  const setMobileNav = useUi((s) => s.setMobileNav)
  const setMenuBack = useUi((s) => s.setMenuBack)
  if (!isInstalledApp()) return null
  /*
   * On a phone, a page opened from the menu goes back to that menu, on the page's own section (owner, 2026-10-19:
   * «ما اعمل عوده من فوق بيطلعني ع سايد بار خالص مش القسم ال دخلت منه»); anything else goes back a page as before.
   */
  const back = () => {
    const phone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches
    if (phone && menuOrigin && pathname === menuOrigin.split('?')[0]) { setMenuBack(true); setMobileNav(true) }
    else navigate(-1)
  }
  return <IconButton label={locale === 'ar' ? 'رجوع' : 'Back'} icon={ArrowLeft} className="rtl:[&_svg]:rotate-180" onClick={back} />
}

export function Breadcrumbs() {
  const { l, t } = useT()
  const active = useActiveNav()
  const tabLink = useTabLink()
  if (!active) return null
  const crumbs = [...active.trail.map((g) => l(g.label)), l(active.page.label)]
  return (
    <ol className="flex min-w-0 items-center gap-1 text-xs text-fg-muted" aria-label="Breadcrumb">
      <li className="hidden sm:block">
        <Link to="/" className="hover:text-fg" {...tabLink('/')}>
          {t('shell.home')}
        </Link>
      </li>
      {crumbs.map((c, i) => (
        <li key={i} className={i < crumbs.length - 1 ? 'hidden min-w-0 items-center gap-1 md:flex' : 'flex min-w-0 items-center gap-1'}>
          <ChevronRight size={12} className="hidden shrink-0 opacity-60 sm:block rtl:rotate-180" aria-hidden />
          <span className={i === crumbs.length - 1 ? 'truncate font-medium text-fg' : 'truncate'} aria-current={i === crumbs.length - 1 ? 'page' : undefined}>
            {c}
          </span>
        </li>
      ))}
    </ol>
  )
}

export function Topbar({ onOpenCommand }: { onOpenCommand: () => void }) {
  const { t, locale } = useT()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const refresh = useAuth((s) => s.refresh)
  const { theme, setTheme, setLocale, density, setDensity, errorSound, setErrorSound } = useUi()
  const badges = useNavBadges()
  const canNotifications = useCan('notifications:read')
  const [pwOpen, setPwOpen] = useState(false)
  const tabLink = useTabLink()
  const narrow = useNarrow()

  const branches = useQuery({ queryKey: ['auth', 'branches'], queryFn: authService.branches, staleTime: 10 * 60_000, enabled: Boolean(user) })
  const branchName = (() => {
    const b = branches.data?.find((x) => Number(pick(x, 'branchID', 'branchId', 'id')) === user?.branchId)
    return b ? String(pick(b, 'branchName', 'name', 'branchNameAr', 'branchNameEn') ?? '') : null
  })()

  const switchBranch = async (id: number) => {
    try {
      await authService.changeBranch(id)
      await refresh()
      await qc.invalidateQueries()
      notify.success(t('shell.switchBranch'))
    } catch (e) {
      notify.error(e)
    }
  }

  // the account is shown by the employee's name, never a letters circle (owner, 2026-10-30)
  const userName = (user?.displayName ?? user?.username ?? '').trim()

  const accountItems: Parameters<typeof Menu>[0]['items'] = [
          { label: `${t('shell.theme')}: ${t('shell.themeLight')}`, icon: Sun, onSelect: () => setTheme('light'), hidden: theme === 'light' },
          { label: `${t('shell.theme')}: ${t('shell.themeDark')}`, icon: Moon, onSelect: () => setTheme('dark'), hidden: theme === 'dark' },
          { label: `${t('shell.theme')}: ${t('shell.themeSystem')}`, icon: Monitor, onSelect: () => setTheme('system'), hidden: theme === 'system' },
          { label: locale === 'ar' ? 'English' : 'العربية', icon: Languages, onSelect: () => setLocale(locale === 'ar' ? 'en' : 'ar') },
          { label: `${t('shell.density')}: ${density === 'compact' ? t('shell.densityComfortable') : t('shell.densityCompact')}`, icon: Rows3, onSelect: () => setDensity(density === 'compact' ? 'comfortable' : 'compact') },
          // The alert that plays on every error (owner, 2026-09-23) — muted per user, remembered on this device.
          {
            label: errorSound ? t('shell.errorSoundOff') : t('shell.errorSoundOn'),
            icon: errorSound ? SpeakerSimpleX : SpeakerSimpleHigh,
            onSelect: () => {
              setErrorSound(!errorSound)
              // Unmuting plays it once, so the user hears what they just turned on.
              if (!errorSound) playAlertSound({ enabled: true, pathname: '/', force: true })
            },
          },
          'separator',
          // his own two-step sign-in and sessions (features/auth/AccountSecurityPage.tsx)
          { label: locale === 'ar' ? 'الأمان وتسجيل الدخول' : 'Sign-in security', icon: ShieldCheck, onSelect: () => navigate('/profile/security') },
          { label: t('auth.changePassword'), icon: KeyRound, onSelect: () => setPwOpen(true) },
          {
            label: t('auth.signOut'),
            icon: LogOut,
            danger: true,
            onSelect: async () => {
              await logout()
              qc.clear()
              useTabs.getState().reset()
              navigate('/login')
            },
          },
        ]

  const branchItems = (branches.data ?? []).map((b) => {
    const id = Number(pick(b, 'branchID', 'branchId', 'id'))
    return { label: String(pick(b, 'branchName', 'name') ?? id), icon: id === user?.branchId ? Check : undefined, onSelect: () => void switchBranch(id) }
  })

  if (narrow) {
    return (
      <>
        <PhoneTopbar
          onOpenCommand={onOpenCommand}
          userName={userName}
          branchName={branchName || (user?.branchId ? `#${user.branchId}` : null)}
          branchItems={(branches.data?.length ?? 0) > 1 ? branchItems : null}
          notifications={canNotifications ? badges.notifications ?? 0 : null}
          accountItems={accountItems}
          accountLabel={user?.displayName ?? user?.username ?? 'Account'}
        />
        <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
      </>
    )
  }

  /*
   * The desktop bar (owner, 2026-10-30: «بروفيشنال ومنظم ويكون بريميم اوي زي الشركات العالمية», design 1): one calm white
   * bar — the search in the middle, then your branch, alerts and you, each group set apart by a hairline. No page name:
   * the sidebar already shows where you are (owner, 2026-10-31: «اسم الصفحه ال ظاهر فوق ده بيبوظ الشكل اصلا مفروض
   * احنا عارفين اي مفتوح من سايد بار»).
   */
  const sep = <span aria-hidden className="mx-1 h-6 w-px shrink-0 bg-border" />
  const branchChip = 'h-9 max-w-64 gap-2 rounded-[10px] border border-border bg-surface px-3 text-[13px] font-bold text-fg-strong shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:bg-surface-hover'
  const dot = <span aria-hidden className="size-2 shrink-0 rounded-full bg-success shadow-[0_0_0_3px_color-mix(in_srgb,var(--success)_18%,transparent)]" />
  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-border bg-surface px-6">
      <BackButton />

      <button
        onClick={onOpenCommand}
        className="mx-auto flex h-10 w-full max-w-[460px] min-w-0 items-center gap-2.5 rounded-[10px] border border-border bg-bg-subtle px-3 text-[13px] text-fg-subtle transition-colors hover:border-border-strong hover:bg-surface"
      >
        <Search size={16} aria-hidden className="shrink-0" />
        <span className="flex-1 truncate text-start">{t('shell.commandHint')}</span>
        <span className="flex shrink-0 gap-1" dir="ltr"><Kbd>Ctrl</Kbd><Kbd>K</Kbd></span>
      </button>
      <InstallButton />

      {/*
        * The branch he works in now, always in sight (owner, 2026-10-25: «تظهر للموظفين فوق الفرع الحالي ال هما فيه»;
        * 3020912 was put on a sheet from the wrong branch without anyone seeing it), said in words with a caret when it can
        * be changed (owner, 2026-10-26: «الموظف يقدر يغير الفرع منين فوق ف سيستم عشان مش لاقيه»).
        */}
      {(branches.data?.length ?? 0) <= 1 && Boolean(branchName || user?.branchId) && (
        <span className={cn('inline-flex items-center', branchChip)} title={locale === 'ar' ? 'حسابك مسموح له بهذا الفرع فقط — لتغييره يجب إضافة الفرع إلى حسابك' : 'Your account is allowed this branch only'}>
          {dot}<span className="shrink-0 font-semibold text-fg-muted">{locale === 'ar' ? 'الفرع:' : 'Branch:'}</span><span className="truncate">{branchName || `#${user?.branchId}`}</span>
        </span>
      )}
      {(branches.data?.length ?? 0) > 1 && (
        <Menu
          trigger={
            <button type="button" className={cn('inline-flex shrink-0 items-center', branchChip)} aria-label={locale === 'ar' ? 'الفرع الحالي — اضغط لتغييره' : 'Current branch — click to change'} title={locale === 'ar' ? 'اضغط لتغيير الفرع' : 'Click to change the branch'}>
              {dot}<span className="shrink-0 font-semibold text-fg-muted">{locale === 'ar' ? 'الفرع:' : 'Branch:'}</span>
              <span className="truncate">{branchName ?? t('shell.branch')}</span>
              <ChevronDown size={12} aria-hidden className="shrink-0 text-fg-muted" />
            </button>
          }
          items={branchItems}
        />
      )}

      {sep}
      {canNotifications && (
        // the badge counts COREX alerts too: it opens the alerts center, where they are
        // (owner, 2026-10-01: "ظاهر ف نوتفكيشن فوق 224 نوتفكيشن بنضغط مفيش حاجه")
        <Link to="/notifications/alerts" {...tabLink('/notifications/alerts')} className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-[10px] text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg-strong" aria-label={locale === 'ar' ? 'الإشعارات' : 'Notifications'}>
          <Bell size={19} aria-hidden />
          {(badges.notifications ?? 0) > 0 && (
            <span className="tabular absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white ring-2 ring-surface">{(badges.notifications ?? 0) > 99 ? '99+' : badges.notifications}</span>
          )}
        </Link>
      )}
      {sep}

      <Menu
        trigger={
          <button className="flex h-10 max-w-56 shrink-0 items-center gap-2 rounded-[10px] px-2 text-start transition-colors hover:bg-surface-hover" aria-label={userName || 'Account'}>
            <span className="min-w-0 leading-tight">
              <b className="block truncate text-[13px] font-bold text-fg-strong">{userName}</b>
              <small className="block truncate text-[11px] font-medium text-fg-muted">{/admin|super/i.test(String(user?.roleKey ?? '')) ? (locale === 'ar' ? 'مدير النظام' : 'Administrator') : (locale === 'ar' ? 'موظف' : 'Staff')}</small>
            </span>
            <ChevronDown size={12} aria-hidden className="shrink-0 text-fg-muted" />
          </button>
        }
        items={accountItems}
      />
      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  )
}

function ChangePasswordModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true)
    try {
      await authService.changePassword(current, next)
      notify.success(t('auth.passwordChanged'))
      onOpenChange(false)
      setCurrent('')
      setNext('')
    } catch (e) {
      notify.error(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t('auth.changePassword')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!current || next.length < 6} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('auth.currentPassword')} required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
        <Field label={t('auth.newPassword')} required hint="≥ 6">{(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}</Field>
      </div>
    </Modal>
  )
}

const NARROW = '(max-width: 767px)'
/** Phones and small tablets: the width the bottom tab bar shows at, where the top bar is the floating capsule. */
function useNarrow() {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const m = window.matchMedia(NARROW)
      m.addEventListener?.('change', cb)
      return () => m.removeEventListener?.('change', cb)
    },
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(NARROW).matches),
    () => false,
  )
}

/**
 * The phone's top bar (owner, 2026-10-30, design 1 «كبسولة زجاجية»): one floating glass capsule — the COREX mark,
 * the branch you work in, then the open tabs, search and alerts in one pill, then you. No page name (owner, 2026-10-31:
 * «اسم الصفحه ال ظاهر فوق ده بيبوظ الشكل … ومبوظ الشكل اكتر ف موبايل»): the bottom bar and the menu say where you are.
 * The brand tile opens the sidebar; back (installed app) and install keep their places in the capsule.
 */
function PhoneTopbar({ onOpenCommand, userName, branchName, branchItems, notifications, accountItems, accountLabel }: {
  onOpenCommand: () => void
  userName: string
  branchName: string | null
  branchItems: Parameters<typeof Menu>[0]['items'] | null
  notifications: number | null
  accountItems: Parameters<typeof Menu>[0]['items']
  accountLabel: string
}) {
  const { locale, t } = useT()
  const ar = locale === 'ar'
  const tabLink = useTabLink()
  const setMobileNav = useUi((s) => s.setMobileNav)
  const count = useTabs((s) => s.tabs.length)
  const [tabsOpen, setTabsOpen] = useState(false)
  const installed = isInstalledApp()

  // the branch he works in, the bar's one line of text (owner, 2026-10-25: always in sight)
  const branchLine = (
    <span className="flex min-w-0 flex-col justify-center leading-tight">
      <span className="text-[11px] font-semibold text-fg-muted">{ar ? 'الفرع' : 'Branch'}</span>
      <span className="flex min-w-0 items-center gap-1 text-[15px] font-extrabold text-fg-strong">
        <span className="truncate">{branchName}</span>
        {branchItems && <ChevronDown size={12} aria-hidden className="shrink-0 text-fg-muted" />}
      </span>
    </span>
  )

  return (
    <header className="sticky top-0 z-30 shrink-0 px-3 pb-2 pt-[calc(env(safe-area-inset-top)+8px)]">
      <div className="flex h-[62px] items-center gap-2.5 rounded-[22px] border border-brand/15 bg-surface/85 px-2.5 shadow-[0_10px_30px_-16px_rgba(30,27,75,0.4)] backdrop-blur-xl">
        {installed && <span className="-me-1 shrink-0 [&_button]:size-9 [&_button]:rounded-xl"><BackButton /></span>}
        {/* the sidebar: the brand tile itself opens it (the menu's own icon on it) */}
        <button type="button" onClick={() => setMobileNav(true)} aria-label={t('shell.toggleSidebar')} className="brand-tile relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[13px] text-white">
          <MenuIcon size={20} weight="bold" aria-hidden className="relative z-[1]" />
        </button>

        <div className="flex min-w-0 flex-1 items-center">
          {branchName && (branchItems ? (
            <Menu
              trigger={<button type="button" className="flex min-w-0 text-start" aria-label={ar ? 'الفرع الحالي — اضغط لتغييره' : 'Current branch — click to change'}>{branchLine}</button>}
              items={branchItems}
            />
          ) : <span className="flex min-w-0" title={ar ? 'حسابك مسموح له بهذا الفرع فقط' : 'Your account is allowed this branch only'}>{branchLine}</span>)}
        </div>

        <div className="flex h-10 shrink-0 items-center rounded-[14px] bg-bg-subtle">
          {/* the open tabs, counted: the sheet to switch between them */}
          <button type="button" onClick={() => setTabsOpen(true)} className="flex size-10 items-center justify-center" aria-label={ar ? `علامات التبويب (${count})` : `Tabs (${count})`}>
            <span className="tabular flex size-[22px] items-center justify-center rounded-[7px] border-2 border-fg text-[11px] font-extrabold leading-none text-fg">{count}</span>
          </button>
          <button type="button" onClick={onOpenCommand} className="flex size-10 items-center justify-center border-s border-border text-fg" aria-label={ar ? 'بحث' : 'Search'}>
            <Search size={19} aria-hidden />
          </button>
          <span className="flex border-s border-border empty:hidden [&>button]:flex [&>button]:size-10 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-none [&>button]:p-0 [&>button]:text-fg [&>button:first-child]:!hidden"><InstallButton /></span>
          {notifications !== null && (
            <Link to="/notifications/alerts" {...tabLink('/notifications/alerts')} className="relative flex size-10 items-center justify-center border-s border-border text-fg" aria-label={ar ? 'الإشعارات' : 'Notifications'}>
              <Bell size={19} aria-hidden />
              {notifications > 0 && (
                <span className="tabular absolute -end-1 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-extrabold text-white ring-2 ring-surface">{notifications > 99 ? '99+' : notifications}</span>
              )}
            </Link>
          )}
        </div>

        <Menu
          trigger={
            // a person icon, so the branch gets the room (owner, 2026-10-31: the branch was cut to «...ur Hub»); his name
            // is the menu's first line
            <button type="button" className="flex size-10 shrink-0 items-center justify-center rounded-[14px] bg-bg-subtle text-fg" aria-label={accountLabel} title={userName}>
              <UserIcon size={20} weight="duotone" aria-hidden />
            </button>
          }
          items={[{ label: userName || accountLabel, icon: UserIcon, onSelect: () => undefined, disabled: true }, 'separator', ...accountItems]}
        />
      </div>
      <MobileTabsSheet open={tabsOpen} onOpenChange={setTabsOpen} onNewTab={onOpenCommand} />
    </header>
  )
}
